import { useQuery } from '@tanstack/react-query';

import {
  PublicSuggestedConnection,
  PublicUserConnection,
  PublicUserConnectionComparison,
} from '@shared/types/userConnection';

import API from '@api/API';

import { useAppContext } from '../contexts/AppContext';
import { useCurrentUser } from './UserQueries';

export const useUserConnectionsQuery = () => {
  const { currentUser } = useCurrentUser();
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserConnectionsQuery.queryKey(currentUser?._id),
    queryFn: () => useUserConnectionsQuery.queryFn(),
    enabled: userIsLoggedIn,
  });

  return {
    connectionsAreReady: isFetched,
    connections: data?.connections ?? [],
    suggestedConnections: data?.suggestedConnections ?? [],
    refetchConnections: refetch,
  };
};
useUserConnectionsQuery.queryKey = (userId: string | undefined) => [
  'user',
  userId ?? 'unauthenticated',
  'connections',
];
useUserConnectionsQuery.queryFn = () =>
  API.get<{
    connections: PublicUserConnection[];
    suggestedConnections: PublicSuggestedConnection[];
  }>(`/v1/user/connections`);

export const useUserConnectionComparisonQuery = (otherUserId: string | undefined) => {
  const { currentUser } = useCurrentUser();

  const { isFetched, data } = useQuery({
    queryKey: useUserConnectionComparisonQuery.queryKey(currentUser?._id, otherUserId),
    queryFn: () => useUserConnectionComparisonQuery.queryFn(otherUserId),
    enabled: !!otherUserId,
  });

  return {
    connectionComparisonIsReady: isFetched,
    connectionComparison: data?.connection,
  };
};
useUserConnectionComparisonQuery.queryKey = (
  currentUserId: string | undefined,
  otherUserId: string | undefined,
) => [
  'user',
  'connection-comparison',
  otherUserId ?? 'undefined',
  currentUserId ?? 'unauthenticated',
];
useUserConnectionComparisonQuery.queryFn = (
  otherUserId: string | undefined,
): Promise<{ connection: PublicUserConnectionComparison | undefined }> =>
  otherUserId
    ? API.get<{ connection: PublicUserConnectionComparison }>(
        `/v1/users/${otherUserId}/connection-comparison`,
      )
    : Promise.resolve({ connection: undefined });
